import { fromJS } from "immutable";

import { withMutations } from "@/redux/modules/helpers/reducerWrappers";
import { sumTotalLinealFeetInMm } from "@/utils/measurement/measurement-aggregate";

/**
 * Action type for adding a lineal feet length. Handled by this wrapper so we don't add
 * new logic to the large additional-products.js module.
 */
export const ADD_LINEAL_FEET_LENGTH = "ADD_LINEAL_FEET_LENGTH";
export const DELETE_LINEAL_FEET_LENGTH = "DELETE_LINEAL_FEET_LENGTH";

const sortItemsByLength = (items, sortLengths) => {
  if (sortLengths === "largest_to_smallest") {
    return [...items].sort((a, b) => parseFloat(b.length || 0) - parseFloat(a.length || 0));
  }
  if (sortLengths === "smallest_to_largest") {
    return [...items].sort((a, b) => parseFloat(a.length || 0) - parseFloat(b.length || 0));
  }
  return items;
};

const getPathConfig = (value) => {
  const { type, itemIndex, cartIndex, subKitIndex } = value;
  if (type === "subKitItem") {
    return {
      basePath: ["items", itemIndex, "subKits", subKitIndex, "kitProducts", cartIndex],
      estimatedKey: "lengthItems",
      hasInitialLengthItems: true,
    };
  }
  if (type === "kitItem") {
    return {
      basePath: ["items", itemIndex, "productKitItems", cartIndex],
      estimatedKey: "lengthItems",
      hasInitialLengthItems: true,
    };
  }
  return {
    basePath: ["items", itemIndex],
    estimatedKey: "subitems",
    hasInitialLengthItems: false,
  };
};

const addProductLengthLogic = (_state, value) => {
  const { amount, length, lengthFt, lengthIn, minLength, sortLengths, isActual } = value;
  const { basePath, estimatedKey, hasInitialLengthItems } = getPathConfig(value);

  const existingItems = _state
    .getIn([...basePath, !isActual ? estimatedKey : "actualLengthItems"], fromJS([]))
    .toJS();
  const items = [...existingItems, { amount, length, lengthFt, lengthIn }];
  const itemsToSet = sortItemsByLength(items, sortLengths);

  const { total, forPrice } = sumTotalLinealFeetInMm({
    items: itemsToSet,
    minLengthInMm: minLength,
  });

  const immutableItems = fromJS(itemsToSet);
  const sumObject = fromJS({ total: parseFloat(total), forPrice: parseFloat(forPrice) });

  if (!isActual) {
    _state.setIn([...basePath, estimatedKey], immutableItems);
    if (hasInitialLengthItems) {
      _state.setIn([...basePath, "initialLengthItems"], immutableItems);
    }
    _state.setIn([...basePath, "sumLengths"], sumObject);
  }

  _state.setIn([...basePath, "actualLengthItems"], immutableItems);
  _state.setIn([...basePath, "actualSumLengths"], sumObject);
};

const deleteProductLengthLogic = (_state, value) => {
  const { subitemIndex, minLength, isActual } = value;
  const { basePath, estimatedKey, hasInitialLengthItems } = getPathConfig(value);

  if (!isActual) {
    _state.deleteIn([...basePath, estimatedKey, subitemIndex]);
    if (hasInitialLengthItems) {
      _state.deleteIn([...basePath, "initialLengthItems", subitemIndex]);
    }
  }

  _state.deleteIn([...basePath, "actualLengthItems", subitemIndex]);

  const items = _state
    .getIn([...basePath, !isActual ? estimatedKey : "actualLengthItems"], fromJS([]))
    .toJS();
  const { total, forPrice } = sumTotalLinealFeetInMm({ items, minLengthInMm: minLength });
  const sumObject = fromJS({ total: parseFloat(total), forPrice: parseFloat(forPrice) });

  if (!isActual) {
    _state.setIn([...basePath, "sumLengths"], sumObject);
  }

  _state.setIn([...basePath, "actualSumLengths"], sumObject);
};

const addProductLengthReducer = withMutations(addProductLengthLogic);
const deleteProductLengthReducer = withMutations(deleteProductLengthLogic);

/**
 * Wraps the original additional-products reducer so that ADD_LINEAL_FEET_LENGTH
 * is handled here instead of in additional-products.js. All other actions are delegated.
 *
 * @param {Function} originalReducer - additionalProducts.reducers
 * @returns {Function} Wrapped reducer for the additionalProducts slice
 */
const createAdditionalProductsWrapper = (originalReducer) => (state, action) => {
  if (action.type === ADD_LINEAL_FEET_LENGTH) {
    return addProductLengthReducer(state, action);
  }
  if (action.type === DELETE_LINEAL_FEET_LENGTH) {
    return deleteProductLengthReducer(state, action);
  }
  return originalReducer(state, action);
};

export default createAdditionalProductsWrapper;
